'use client';

import React, { useState, useEffect } from 'react';
import RegionCard from './RegionCard';
import CountryDetailPanel from './CountryDetailPanel';
import InteractiveMap from './InteractiveMap';
import SeasonalCalendar from './SeasonalCalendar';
import BorderCrossingEstimator from './BorderCrossingEstimator';
import RegulatoryUpdates from './RegulatoryUpdates';
import LocalPartnerDirectory from './LocalPartnerDirectory';

interface Region {
  id: string;
  name: string;
  countries: string[];
  image: string;
  alt: string;
  coverage: string;
  activeRoutes: number;
  borderCrossings: number;
  certifications: string[];
}

interface MapRegion {
  id: string;
  name: string;
  coordinates: string;
  color: string;
}

interface CountryDetail {
  name: string;
  capital: string;
  languages: string[];
  currency: string;
  timezone: string;
  borderCrossings: Array<{
    name: string;
    avgWaitTime: string;
    operatingHours: string;
  }>;
  regulations: string[];
  seasonalConsiderations: string[];
  localPartners: number;
}

interface SeasonalPeriod {
  month: string;
  conditions: 'optimal' | 'moderate' | 'challenging';
  description: string;
  icon: string;
}

interface BorderCrossing {
  id: string;
  name: string;
  from: string;
  to: string;
  avgTime: string;
  peakTime: string;
  documents: string[];
}

interface RegulatoryUpdate {
  id: string;
  title: string;
  country: string;
  date: string;
  type: 'new' | 'updated' | 'alert';
  description: string;
}

interface LocalPartner {
  id: string;
  name: string;
  type: string;
  country: string;
  services: string[];
  contact: string;
  image: string;
  alt: string;
  certifications: string[];
}

const RegionalCoverageInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<CountryDetail | null>(
    null
  );

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const regions: Region[] = [
  {
    id: 'east-africa',
    name: 'East Africa',
    countries: ['Kenya', 'Tanzania', 'Uganda'],
    image: "https://images.unsplash.com/photo-1571950392377-757a7aae6874",
    alt: 'Aerial view of East African savanna landscape with acacia trees and rolling hills under blue sky',
    coverage: 'Complete logistics coverage',
    activeRoutes: 45,
    borderCrossings: 12,
    certifications: ['ISO 9001', 'OHSAS 18001', 'ISO 14001']
  },
  {
    id: 'central-africa',
    name: 'Central Africa',
    countries: ['DRC', 'Rwanda', 'Burundi'],
    image: "https://images.unsplash.com/photo-1561389745-97596a161957",
    alt: 'Dense tropical rainforest canopy in Central Africa with morning mist and lush green vegetation',
    coverage: 'Mining & heavy equipment specialists',
    activeRoutes: 38,
    borderCrossings: 9,
    certifications: ['ISO 9001', 'OHSAS 18001', 'Mining Safety']
  },
  {
    id: 'southern-africa',
    name: 'Southern Africa',
    countries: ['Zambia', 'Zimbabwe', 'Mozambique'],
    image: "https://images.unsplash.com/photo-1703604787785-e9ed9639ea6c",
    alt: 'Southern African plateau landscape with red soil, scattered baobab trees and distant mountains',
    coverage: 'Cross-border trade corridors',
    activeRoutes: 52,
    borderCrossings: 15,
    certifications: ['ISO 9001', 'SADC Certified', 'Customs AEO']
  }];


  const mapRegions: MapRegion[] = [
  {
    id: 'east-africa',
    name: 'East Africa',
    coordinates:
    'M 450 150 L 500 140 L 520 180 L 510 220 L 480 240 L 440 220 Z',
    color: '#1B365D'
  },
  {
    id: 'central-africa',
    name: 'Central Africa',
    coordinates:
    'M 350 180 L 420 170 L 440 220 L 420 260 L 370 270 L 330 240 Z',
    color: '#2C3E50'
  },
  {
    id: 'southern-africa',
    name: 'Southern Africa',
    coordinates:
    'M 370 280 L 440 270 L 460 310 L 450 360 L 400 380 L 350 350 Z',
    color: '#34495E'
  }];


  const countryDetails: Record<string, CountryDetail> = {
    Kenya: {
      name: 'Kenya',
      capital: 'Nairobi',
      languages: ['English', 'Swahili'],
      currency: 'KES',
      timezone: 'EAT (UTC+3)',
      borderCrossings: [
      {
        name: 'Malaba Border',
        avgWaitTime: '2-3 hours',
        operatingHours: '24/7'
      },
      {
        name: 'Busia Border',
        avgWaitTime: '1-2 hours',
        operatingHours: '24/7'
      },
      {
        name: 'Namanga Border',
        avgWaitTime: '1.5-2.5 hours',
        operatingHours: '6 AM - 10 PM'
      }],

      regulations: [
      'COMESA Certificate of Origin required for regional trade',
      'Kenya Revenue Authority (KRA) pre-clearance mandatory',
      'Phytosanitary certificates for agricultural products',
      'Dangerous goods require special permits from NTSA'],

      seasonalConsiderations: [
      'Long rains (March-May) may affect rural road conditions',
      'Short rains (October-December) can cause delays in coastal regions'],

      localPartners: 8
    },
    DRC: {
      name: 'Democratic Republic of Congo',
      capital: 'Kinshasa',
      languages: ['French', 'Lingala', 'Swahili'],
      currency: 'CDF',
      timezone: 'WAT/CAT (UTC+1/+2)',
      borderCrossings: [
      {
        name: 'Kasumbalesa Border',
        avgWaitTime: '4-6 hours',
        operatingHours: '6 AM - 6 PM'
      },
      {
        name: 'Goma-Gisenyi Border',
        avgWaitTime: '2-4 hours',
        operatingHours: '6 AM - 8 PM'
      }],

      regulations: [
      'Mining export permits required from Ministry of Mines',
      'DGDA customs clearance with certified documentation',
      'Security escorts mandatory for certain routes',
      'Environmental compliance certificates for extractives'],

      seasonalConsiderations: [
      'Rainy season (September-May) severely impacts road conditions',
      'Dry season (June-August) optimal for heavy equipment transport'],

      localPartners: 12
    },
    Tanzania: {
      name: 'Tanzania',
      capital: 'Dodoma',
      languages: ['Swahili', 'English'],
      currency: 'TZS',
      timezone: 'EAT (UTC+3)',
      borderCrossings: [
      {
        name: 'Tunduma Border',
        avgWaitTime: '3-4 hours',
        operatingHours: '24/7'
      },
      {
        name: 'Namanga Border',
        avgWaitTime: '1.5-2.5 hours',
        operatingHours: '6 AM - 10 PM'
      }],

      regulations: [
      'TRA electronic cargo tracking system (ECTS) mandatory',
      'EAC Certificate of Origin for regional trade',
      'Port clearance documentation for Dar es Salaam imports',
      'Transit bond requirements for landlocked destinations'],

      seasonalConsiderations: [
      'Long rains (March-May) affect central corridor routes',
      'Short rains (November-December) impact northern regions'],

      localPartners: 10
    }
  };

  const seasonalPeriods: SeasonalPeriod[] = [
  {
    month: 'January',
    conditions: 'optimal',
    description: 'Dry season with excellent road conditions across all regions',
    icon: 'SunIcon'
  },
  {
    month: 'February',
    conditions: 'optimal',
    description: 'Peak logistics season with minimal weather disruptions',
    icon: 'SunIcon'
  },
  {
    month: 'March',
    conditions: 'moderate',
    description: 'Long rains begin affecting East African routes',
    icon: 'CloudIcon'
  },
  {
    month: 'April',
    conditions: 'challenging',
    description: 'Heavy rains may cause delays in rural and mining areas',
    icon: 'CloudIcon'
  },
  {
    month: 'May',
    conditions: 'moderate',
    description: 'Rains decreasing, road conditions improving',
    icon: 'CloudIcon'
  },
  {
    month: 'June',
    conditions: 'optimal',
    description: 'Dry season returns with excellent logistics conditions',
    icon: 'SunIcon'
  },
  {
    month: 'July',
    conditions: 'optimal',
    description: 'Peak dry season ideal for heavy equipment transport',
    icon: 'SunIcon'
  },
  {
    month: 'August',
    conditions: 'optimal',
    description: 'Continued excellent conditions across all corridors',
    icon: 'SunIcon'
  },
  {
    month: 'September',
    conditions: 'optimal',
    description: 'Last month of optimal dry season conditions',
    icon: 'SunIcon'
  },
  {
    month: 'October',
    conditions: 'moderate',
    description: 'Short rains begin in some regions',
    icon: 'CloudIcon'
  },
  {
    month: 'November',
    conditions: 'moderate',
    description: 'Short rains affecting northern routes',
    icon: 'CloudIcon'
  },
  {
    month: 'December',
    conditions: 'optimal',
    description: 'Rains clearing, good conditions for year-end shipments',
    icon: 'SunIcon'
  }];


  const borderCrossings: BorderCrossing[] = [
  {
    id: 'malaba',
    name: 'Malaba Border',
    from: 'Kenya',
    to: 'Uganda',
    avgTime: '2-3 hours',
    peakTime: '4-6 hours',
    documents: [
    'Valid passport or national ID',
    'Commercial invoice and packing list',
    'Certificate of Origin (COMESA)',
    'Bill of Lading or Airway Bill',
    'Import/Export permits as required',
    'Insurance certificate']

  },
  {
    id: 'kasumbalesa',
    name: 'Kasumbalesa Border',
    from: 'Zambia',
    to: 'DRC',
    avgTime: '4-6 hours',
    peakTime: '8-12 hours',
    documents: [
    'Valid passport with visa',
    'Mining export permits (if applicable)',
    'DGDA customs declaration',
    'Certificate of Origin',
    'Phytosanitary certificates',
    'Security clearance documentation',
    'Transit insurance']

  },
  {
    id: 'tunduma',
    name: 'Tunduma Border',
    from: 'Tanzania',
    to: 'Zambia',
    avgTime: '3-4 hours',
    peakTime: '6-8 hours',
    documents: [
    'Valid passport or EAC travel document',
    'TRA electronic cargo tracking seal',
    'Certificate of Origin (EAC/COMESA)',
    'Commercial invoice',
    'Transit bond (if applicable)',
    'Vehicle registration documents']

  }];


  const regulatoryUpdates: RegulatoryUpdate[] = [
  {
    id: 'update-1',
    title: 'New Electronic Cargo Tracking Requirements',
    country: 'Tanzania',
    date: '15 Dec 2025',
    type: 'new',
    description:
    'TRA has implemented mandatory electronic cargo tracking seals for all commercial vehicles entering Tanzania. Compliance deadline: 1 February 2026.'
  },
  {
    id: 'update-2',
    title: 'Updated Mining Export Documentation',
    country: 'DRC',
    date: '10 Dec 2025',
    type: 'updated',
    description:
    'Ministry of Mines has revised export permit procedures for copper and cobalt. New environmental compliance certificates now required.'
  },
  {
    id: 'update-3',
    title: 'Border Closure Alert - Malaba',
    country: 'Kenya',
    date: '28 Dec 2025',
    type: 'alert',
    description:
    'Malaba border will be closed for infrastructure upgrades from 5-7 January 2026. Alternative routing via Busia recommended.'
  },
  {
    id: 'update-4',
    title: 'COMESA Certificate Digitalization',
    country: 'Regional',
    date: '20 Dec 2025',
    type: 'updated',
    description:
    'COMESA has launched digital Certificate of Origin system. Paper certificates will be phased out by June 2026.'
  }];


  const localPartners: LocalPartner[] = [
  {
    id: 'partner-1',
    name: 'East Africa Customs Brokers',
    type: 'Customs',
    country: 'Kenya',
    services: ['Customs Clearance', 'Documentation', 'Compliance'],
    contact: '+254-700-123456',
    image: "https://images.unsplash.com/photo-1714990925326-50ad8800d982",
    alt: 'Modern glass office building in Nairobi business district with blue sky background',
    certifications: ['AEO', 'KRA Licensed', 'ISO 9001']
  },
  {
    id: 'partner-2',
    name: 'Central Africa Security Services',
    type: 'Security',
    country: 'DRC',
    services: ['Armed Escorts', 'Route Security', 'Asset Protection'],
    contact: '+243981944976',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_154c22e04-1767100728770.png",
    alt: 'Professional security personnel in uniform standing at industrial facility entrance',
    certifications: ['Licensed', 'Insured', 'Mining Approved']
  },
  {
    id: 'partner-3',
    name: 'Tanzania Warehouse Solutions',
    type: 'Warehouse',
    country: 'Tanzania',
    services: ['Storage', 'Distribution', 'Inventory Management'],
    contact: '+255-700-789012',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17c6854d1-1766843809238.png",
    alt: 'Large modern warehouse interior with high ceilings and organized storage racks',
    certifications: ['ISO 9001', 'Bonded', 'Climate Controlled']
  },
  {
    id: 'partner-4',
    name: 'Uganda Transport Network',
    type: 'Transport',
    country: 'Uganda',
    services: ['Fleet Management', 'Cross-Border', 'Heavy Haulage'],
    contact: '+256-700-345678',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bc9f3e1a-1766836068588.png",
    alt: 'Fleet of modern cargo trucks parked in organized rows at transport depot',
    certifications: ['NTSA Approved', 'GPS Tracked', 'Insured']
  }];


  const handleRegionSelect = (regionId: string) => {
    setSelectedRegion(regionId);
    setSelectedCountry(null);
  };

  const handleCountrySelect = (countryName: string) => {
    if (countryDetails[countryName]) {
      setSelectedCountry(countryDetails[countryName]);
    }
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-1/3" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) =>
              <div key={i} className="h-96 bg-muted rounded-lg" />
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="space-y-12">
      <section>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {regions.map((region) =>
          <RegionCard
            key={region.id}
            region={region}
            onSelect={handleRegionSelect}
            isSelected={selectedRegion === region.id} />

          )}
        </div>
      </section>

      <section>
        <InteractiveMap
          regions={mapRegions}
          selectedRegion={selectedRegion}
          onRegionSelect={handleRegionSelect} />

      </section>

      {selectedCountry &&
      <section>
          <CountryDetailPanel
          country={selectedCountry}
          onClose={() => setSelectedCountry(null)} />

        </section>
      }

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {regions.
        find((r) => r.id === selectedRegion)?.
        countries.slice(0, 3).
        map((country) =>
        <button
          key={country}
          onClick={() => handleCountrySelect(country)}
          className="p-6 bg-card rounded-lg border border-border hover:border-golden-mid hover:shadow-elevation-2 transition-all duration-300 text-left">

              <h3 className="font-headline font-bold text-lg text-foreground mb-2">
                {country}
              </h3>
              <p className="font-body text-sm text-muted-foreground">
                Click to view detailed logistics information
              </p>
            </button>
        )}
      </section>

      <section>
        <SeasonalCalendar periods={seasonalPeriods} />
      </section>

      <section>
        <BorderCrossingEstimator crossings={borderCrossings} />
      </section>

      <section>
        <RegulatoryUpdates updates={regulatoryUpdates} />
      </section>

      <section>
        <LocalPartnerDirectory partners={localPartners} />
      </section>
    </div>);

};

export default RegionalCoverageInteractive;